/*
 * Персист сессии оператора (токен control-plane) в localStorage с TTL.
 *
 * По умолчанию проект держал токен ТОЛЬКО в памяти (§1.12) — на обновление страницы
 * требовалась повторная авторизация. По явной просьбе оператора храним токен в браузере
 * на срок жизни (сутки), чтобы рефреш не разлогинивал. Трейд-офф: localStorage доступен
 * JS-коду страницы (риск при XSS) — снижен строгим CSP (`default-src 'self'`, без сторонних
 * скриптов) + HTTPS + firewall; для одно-операторского инструмента приемлемо. Токен всё равно
 * валидируется запросом при восстановлении, истекает по TTL и стирается при logout/401.
 */

const SESSION_KEY = 'fram.session'
const SESSION_TTL_MS = 24 * 60 * 60 * 1000 // сутки

interface StoredSession {
  token: string
  /** Эпоха (мс), после которой сессия считается истёкшей и стирается. */
  expiresAt: number
}

/** Сохранить токен на сутки (или дольше при ре-логине). Сбой хранилища (приватный режим/
 * квота) глотаем — персист best-effort, в памяти токен всё равно есть. */
export function saveSession(token: string): void {
  try {
    const data: StoredSession = { token, expiresAt: Date.now() + SESSION_TTL_MS }
    localStorage.setItem(SESSION_KEY, JSON.stringify(data))
  } catch {
    /* localStorage недоступен — работаем как раньше (только память) */
  }
}

/** Прочитать валидный (не истёкший) токен или ``null``. Истёкший/битый — стираем. */
export function loadSession(): string | null {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(SESSION_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const data = JSON.parse(raw) as Partial<StoredSession>
    if (
      typeof data.token !== 'string' ||
      !data.token ||
      typeof data.expiresAt !== 'number' ||
      Date.now() > data.expiresAt
    ) {
      clearSession()
      return null
    }
    return data.token
  } catch {
    clearSession()
    return null
  }
}

/** Есть ли вообще запись сессии (без валидации/чтения токена) — для решения «показывать ли
 * экран восстановления вместо мигания формы логина» на старте. */
export function hasStoredSession(): boolean {
  try {
    return localStorage.getItem(SESSION_KEY) !== null
  } catch {
    return false
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY)
  } catch {
    /* ignore */
  }
}

// --- Активная вкладка (segment-control) -------------------------------------
const ROUTE_KEY = 'fram.route'

/** Запомнить выбранную вкладку, чтобы рефреш страницы не сбрасывал её на «Парсинг». */
export function saveRoute(route: string): void {
  try {
    localStorage.setItem(ROUTE_KEY, route)
  } catch {
    /* localStorage недоступен — маршрут останется только в памяти */
  }
}

/** Прочитать сохранённую вкладку (или ``null``, если не задана/недоступна). */
export function loadRoute(): string | null {
  try {
    return localStorage.getItem(ROUTE_KEY)
  } catch {
    return null
  }
}
